import { useMemo, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import {
	AlignLeft,
	AtSign,
	Calendar,
	CheckSquare,
	ChevronDown,
	Circle,
	File,
	Files,
	Flag,
	Globe,
	Hash,
	Layers,
	Link2,
	List,
	ListChecks,
	Search,
	Type,
	UserSquare,
	Users,
} from 'lucide-react'
import { FormFieldType } from '@kfolks/types'
import { Input } from '@/shared/components/atoms/ui/input'

/**
 * Field palette for the visual form builder.
 *
 * Enum-driven (per the `request-center-form-builder` capability): every
 * value in `FormFieldType` from `@kfolks/types` is rendered as a draggable
 * item, so adding a new value to the enum makes it appear here without a
 * code change. This includes `PROVIDER_ACCESS_LIST` (added late in Spec 1
 * for Team Member Onboarding + Privileged Access).
 *
 * Includes an inline filter input (per design Q4 resolved): case-insensitive
 * substring match against both the human-readable label AND the enum value.
 * "auto" matches `autocomplete` / `autocomplete_multi`; "user picker"
 * matches `USER_PICKER` / `USER_PICKER_MULTI`. Clearing the filter restores
 * the full palette. Drag-and-drop behaviour for visible items is unchanged.
 *
 * Keyboard nav across filtered results is a follow-up; the first cut is a
 * passive filter only.
 *
 * Drag wiring uses `@dnd-kit/core`'s `useDraggable`. The drop target (form
 * canvas, in task 7.3) reads the `data` block off the drop event to decide
 * what to insert. The data shape is `{ source: 'palette', fieldType }` so
 * canvas drops from the palette can be distinguished from canvas-internal
 * reorder drags later.
 */
export function FieldPalette({ idPrefix = 'field-palette' }: { idPrefix?: string }) {
	const [filter, setFilter] = useState('')

	const items = useMemo(() => {
		const lowered = filter.trim().toLowerCase()
		const all = PALETTE_ITEMS
		if (lowered === '') return all
		return all.filter((item) => {
			const labelMatch = item.label.toLowerCase().includes(lowered)
			const valueMatch = item.fieldType.toLowerCase().includes(lowered)
			return labelMatch || valueMatch
		})
	}, [filter])

	return (
		<div className="space-y-2" role="region" aria-label="Field palette">
			<div className="relative">
				<Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
				<Input
					type="text"
					value={filter}
					onChange={(e) => setFilter(e.target.value)}
					placeholder="Filter field types…"
					className="pl-8"
					aria-label="Filter field types"
				/>
			</div>

			<ul className="space-y-1" role="list">
				{items.length === 0 ? (
					<li className="rounded-md border border-dashed p-3 text-center text-sm text-muted-foreground">
						No field types match {`"${filter}"`}
					</li>
				) : (
					items.map((item) => (
						<PaletteItem
							key={item.fieldType}
							idPrefix={idPrefix}
							fieldType={item.fieldType}
							label={item.label}
							icon={item.icon}
						/>
					))
				)}
			</ul>
		</div>
	)
}

// ─── Single palette item ─────────────────────────────────────────────────────

function PaletteItem({
	idPrefix,
	fieldType,
	label,
	icon: Icon,
}: {
	idPrefix: string
	fieldType: FormFieldType
	label: string
	icon: React.ComponentType<{ className?: string }>
}) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `${idPrefix}-${fieldType}`,
		data: { source: 'palette', fieldType },
	})

	return (
		<li>
			<button
				ref={setNodeRef}
				type="button"
				className={
					'flex w-full items-center gap-2 rounded-md border bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
					(isDragging ? 'opacity-50' : '')
				}
				aria-label={`Drag to add ${label} field`}
				data-field-type={fieldType}
				{...listeners}
				{...attributes}
			>
				<Icon className="size-4 shrink-0 text-muted-foreground" />
				<span className="flex-1">{label}</span>
				<span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground/70">{fieldType}</span>
			</button>
		</li>
	)
}

// ─── Palette items table ─────────────────────────────────────────────────────

interface PaletteEntry {
	fieldType: FormFieldType
	label: string
	icon: React.ComponentType<{ className?: string }>
}

/**
 * Single source of truth for palette ordering, labels, and icons.
 *
 * Ordered by authoring frequency: text-y fields first (most common), then
 * structured selects, then advanced pickers, then composite/file fields,
 * with `PROVIDER_ACCESS_LIST` last (specialty pair-picker).
 *
 * Strict exhaustiveness check below ensures every `FormFieldType` enum
 * value has an entry — adding a new value to the enum without updating
 * this table is a compile-time error.
 */
const PALETTE_ITEMS: ReadonlyArray<PaletteEntry> = [
	{ fieldType: FormFieldType.TEXT, label: 'Text', icon: Type },
	{ fieldType: FormFieldType.TEXTAREA, label: 'Long text', icon: AlignLeft },
	{ fieldType: FormFieldType.NUMBER, label: 'Number', icon: Hash },
	{ fieldType: FormFieldType.EMAIL, label: 'Email', icon: AtSign },
	{ fieldType: FormFieldType.DATE, label: 'Date', icon: Calendar },
	{ fieldType: FormFieldType.SELECT, label: 'Select', icon: ChevronDown },
	{ fieldType: FormFieldType.MULTISELECT, label: 'Multi-select', icon: List },
	{ fieldType: FormFieldType.AUTOCOMPLETE, label: 'Autocomplete', icon: Search },
	{ fieldType: FormFieldType.AUTOCOMPLETE_MULTI, label: 'Autocomplete (multi)', icon: ListChecks },
	{ fieldType: FormFieldType.CHECKBOX, label: 'Checkbox', icon: CheckSquare },
	{ fieldType: FormFieldType.RADIO, label: 'Radio', icon: Circle },
	{ fieldType: FormFieldType.USER_PICKER, label: 'User picker', icon: UserSquare },
	{ fieldType: FormFieldType.USER_PICKER_MULTI, label: 'User picker (multi)', icon: Users },
	{ fieldType: FormFieldType.PROJECT_PICKER, label: 'Project picker', icon: Layers },
	{ fieldType: FormFieldType.COUNTRY_PICKER, label: 'Country picker', icon: Globe },
	{ fieldType: FormFieldType.FILE, label: 'File upload', icon: File },
	{ fieldType: FormFieldType.FILE_MULTI, label: 'File upload (multi)', icon: Files },
	{ fieldType: FormFieldType.PRIORITY, label: 'Priority', icon: Flag },
	{ fieldType: FormFieldType.PROVIDER_ACCESS_LIST, label: 'Provider access list', icon: Link2 },
]

// Compile-time exhaustiveness: if a new value is added to FormFieldType
// without a matching PALETTE_ITEMS entry, this assertion fails the build.
// The check is structural (entry exists per enum value, regardless of
// order); ordering is whatever PALETTE_ITEMS declares.
type _ExhaustiveCheck = Record<FormFieldType, true>
const _ASSERT_EXHAUSTIVE: _ExhaustiveCheck = Object.fromEntries(
	PALETTE_ITEMS.map((item) => [item.fieldType, true]),
) as _ExhaustiveCheck
// Reference the assertion so unused-var rules don't flag it.
void _ASSERT_EXHAUSTIVE
